import { useState } from 'react';
import { Field } from './Field';
import { useSubmitReservation } from '../data/queries';
import { gaEvent } from '../lib/analytics';
import { fbqTrack } from '../lib/metaPixel';

/**
 * "Réserver votre place" modal for a formation whose dates aren't confirmed yet.
 * Light interest-capture (no session, no payment) → writes a `reservations` doc.
 */
export function ReservationDialog({
  formationId,
  formationTitle,
  onClose,
}: {
  formationId: string;
  formationTitle: string;
  onClose: () => void;
}) {
  const submit = useSubmitReservation();
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!prenom.trim()) e.prenom = 'Requis';
    if (!nom.trim()) e.nom = 'Requis';
    if (!email || !/.+@.+\..+/.test(email)) e.email = 'Email invalide';
    if (!telephone || telephone.replace(/\D/g, '').length < 8) e.telephone = 'Téléphone invalide';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const send = async () => {
    if (!validate()) return;
    setSubmitError(null);
    try {
      await submit.mutateAsync({
        formationId,
        profile: {
          firstName: prenom.trim(),
          lastName: nom.trim(),
          email: email.trim(),
          phone: telephone.trim(),
        },
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      gaEvent('reservation_complete', { formation_id: formationId });
      fbqTrack('Lead', { content_name: formationTitle, content_ids: [formationId] });
      setDone(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message.replace(/^Firebase: /, '') : String(err),
      );
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(15,25,22,0.55)',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 16,
          maxWidth: 480,
          width: '100%',
          padding: '28px 28px 24px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        }}
      >
        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>✓</div>
            <h3
              style={{
                fontFamily: 'var(--mtc-font-display)',
                fontSize: 24,
                margin: '12px 0 8px',
                color: 'var(--mtc-primary)',
              }}
            >
              Réservation enregistrée
            </h3>
            <p style={{ color: 'var(--mtc-ink-2)', lineHeight: 1.6, fontSize: 15 }}>
              Merci {prenom.trim()} ! Votre place pour <strong>{formationTitle}</strong> est
              pré-réservée. Dès que les dates seront confirmées, l'équipe MTC vous contactera en
              priorité au <strong>{telephone.trim()}</strong> ou à <strong>{email.trim()}</strong>.
            </p>
            <button
              type="button"
              className="mtc-btn mtc-btn-primary mtc-btn-block"
              style={{ marginTop: 20 }}
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 4 }}>
              <span className="mtc-eyebrow">Pré-réservation</span>
            </div>
            <h3
              style={{
                fontFamily: 'var(--mtc-font-display)',
                fontSize: 24,
                margin: '2px 0 6px',
                lineHeight: 1.2,
              }}
            >
              Réservez votre place
            </h3>
            <p style={{ color: 'var(--mtc-ink-2)', fontSize: 14, lineHeight: 1.55, margin: '0 0 20px' }}>
              Les dates de <strong>{formationTitle}</strong> seront bientôt annoncées. Laissez vos
              coordonnées : vous serez contacté·e <strong>en priorité</strong> dès leur confirmation.
              Sans engagement.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Prénom" value={prenom} onChange={setPrenom} error={errors.prenom} autoComplete="given-name" />
              <Field label="Nom" value={nom} onChange={setNom} error={errors.nom} autoComplete="family-name" />
            </div>
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              error={errors.email}
              inputMode="email"
              autoComplete="email"
              placeholder="prenom.nom@exemple.com"
            />
            <Field
              label="Téléphone / WhatsApp"
              type="tel"
              value={telephone}
              onChange={setTelephone}
              error={errors.telephone}
              placeholder="+212 6 00 00 00 00"
              inputMode="tel"
              autoComplete="tel"
            />
            <Field
              label="Message (optionnel)"
              value={message}
              onChange={setMessage}
              placeholder="Une question, une préférence de dates…"
            />

            {submitError && (
              <p className="ins-error" role="alert" style={{ marginTop: 8 }}>
                {submitError}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button type="button" className="mtc-btn mtc-btn-ghost" onClick={onClose}>
                Annuler
              </button>
              <button
                type="button"
                className="mtc-btn mtc-btn-primary mtc-btn-block"
                onClick={send}
                disabled={submit.isPending}
              >
                {submit.isPending ? 'Envoi…' : 'Réserver ma place'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
